/* eslint-disable @typescript-eslint/no-unsafe-call */
import { setCookie } from '@ahm/api-wrappers-core';
import { local } from '@ahm/common-helpers';
import CryptoJS from 'crypto-js';
import { ROUTES } from '../router';
import {
  AuthStatus,
  AuthTab,
  COOKIE_NAME__X_CLIENT_ID,
  COOKIE_NAME__X_DEVICE_ID,
  COOKIE_NAME__X_PHONE,
  COUNT_DOWN_TIME__REQUEST_RESET_PASSCODE,
  COUNT_DOWN_TIME__REQUEST_SIGNIN,
  LOCAL_STORAGE_KEY__FINGERPRINT,
  LOCAL_STORAGE_KEY__SENT_ACTIVATE_CODE_PAYLOAD,
} from './constants';

export interface SocketResponse {
  status: string;
  type: string;
  value: string;
}

export const parseVietnamesePhoneNumber = (phoneNumber: string): string => {
  const countryCode = '84';
  let sanitizedNumber = phoneNumber.replace(/\D/g, '');

  // Remove leading zero or country code if present
  if (sanitizedNumber.startsWith('0')) {
    sanitizedNumber = sanitizedNumber.slice(1);
  } else if (sanitizedNumber.startsWith(countryCode)) {
    sanitizedNumber = sanitizedNumber.slice(countryCode.length);
  } else if (sanitizedNumber.startsWith(`+${countryCode}`)) {
    sanitizedNumber = sanitizedNumber.slice(countryCode.length + 1);
  }

  return `${countryCode}${sanitizedNumber}`;
};

export type SocketClientConfig = {
  domain?: string;
  url: string;
  clientId: string;
};

export const initSocket = async (
  mobile: string,
  { url, clientId }: SocketClientConfig
) => {
  const visitorId = local.getItem(LOCAL_STORAGE_KEY__FINGERPRINT);

  return new Promise<SocketResponse>((resolve, reject) => {
    setCookie(COOKIE_NAME__X_CLIENT_ID, clientId ?? 'web');
    setCookie(COOKIE_NAME__X_DEVICE_ID, visitorId ?? 'web');
    setCookie(COOKIE_NAME__X_PHONE, mobile);

    const socket = new WebSocket(url ?? '');

    socket.onopen = function () {
      console.log('[WebSocket] Connected');
    };

    socket.onmessage = function (event) {
      resolve(JSON.parse(event.data));
    };

    socket.onclose = function (event) {
      reject(event);
    };

    socket.onerror = function (error) {
      reject(error);
      console.log('[WebSocket] Error:', error);
    };
  });
};

export const getRemainCountDownTime = (type = '') => {
  let defaultCountDownTime = 0;
  if (type === 'passcode') {
    defaultCountDownTime = COUNT_DOWN_TIME__REQUEST_RESET_PASSCODE;
  } else {
    defaultCountDownTime = COUNT_DOWN_TIME__REQUEST_SIGNIN;
  }

  const pastSentCodePayload = local.getItem(
    LOCAL_STORAGE_KEY__SENT_ACTIVATE_CODE_PAYLOAD
  );
  if (pastSentCodePayload) {
    const timeLastSend = JSON.parse(pastSentCodePayload);

    if (timeLastSend?.time) {
      const pastTime = Math.ceil(
        (new Date().getTime() - parseInt(timeLastSend.time)) / 1000
      );

      const remainTime = defaultCountDownTime - pastTime;

      if (remainTime > 0) {
        return remainTime;
      }
    }
  }
  return defaultCountDownTime;
};

export const checkSpamSignInPhoneNumber = (mobile: string) => {
  const defaultCountDownTime = COUNT_DOWN_TIME__REQUEST_SIGNIN;
  const prevSentCodePayload = local.getItem(
    LOCAL_STORAGE_KEY__SENT_ACTIVATE_CODE_PAYLOAD
  );

  if (prevSentCodePayload) {
    try {
      const { _id, time, ...rest } = JSON.parse(prevSentCodePayload);
      if (_id === mobile && time) {
        const elapsedTime = Math.ceil((Date.now() - parseInt(time)) / 1000);
        const remainingTime = defaultCountDownTime - elapsedTime;

        // Check for spam based on remaining time
        if (remainingTime > 0) {
          return [true, { _id, time, ...rest }];
        }
      }
    } catch (error) {
      console.error('Failed to parse previous sent code payload:', error);
    }
  }

  return [false];
};

const secretKey = CryptoJS.lib.WordArray.random(32).toString(); // Store this securely

// Function to encode in URL-safe base64
export function toUrlSafeBase64(wordArray: CryptoJS.lib.WordArray): string {
  return CryptoJS.enc.Base64.stringify(wordArray)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

// Function to decode from URL-safe base64
export function fromUrlSafeBase64(base64: string): CryptoJS.lib.WordArray {
  base64 = base64.replace(/-/g, '+').replace(/_/g, '/');
  while (base64.length % 4) {
    base64 += '=';
  }
  return CryptoJS.enc.Base64.parse(base64);
}

// Encrypt function with expiration
export function encrypt(text: string): string {
  const currentTime = Date.now().toString();
  const dataToEncrypt = `${currentTime}:${text}`;
  const iv = CryptoJS.lib.WordArray.random(16);
  const encrypted = CryptoJS.AES.encrypt(
    dataToEncrypt,
    CryptoJS.enc.Hex.parse(secretKey),
    { iv }
  );

  return toUrlSafeBase64(iv) + '.' + encrypted.toString();
}

// Decrypt function with expiration check
export function decrypt(hash: string): string | null {
  const parts = hash.split('.');
  const iv = fromUrlSafeBase64(parts.shift()!);
  const encryptedText = parts.join('.');
  const decrypted = CryptoJS.AES.decrypt(
    encryptedText,
    CryptoJS.enc.Hex.parse(secretKey),
    { iv }
  );

  const decryptedText = decrypted.toString(CryptoJS.enc.Utf8);
  const [timestamp, originalText] = decryptedText.split(':');
  const currentTime = Date.now();
  const timeDifference = currentTime - parseInt(String(timestamp), 10);

  // Check if the encrypted data is older than 5 minutes (300000 ms)
  if (timeDifference > 300000) {
    console.log('Decryption failed: data has expired.');
    return null;
  }

  return String(originalText);
}

// Example usage
// const registerEmail = 'email@example.com';
// const encryptedEmail = encrypt(registerEmail);
// console.log('Encrypted:', encryptedEmail);

// const decryptedEmail = decrypt(encryptedEmail);
// if (decryptedEmail) {
//   console.log('Decrypted:', decryptedEmail);
// } else {
//   console.log('Decryption failed: data has expired or is invalid.');
// }

export function getCurrentLang(url: string): string | null {
  try {
    const parsedUrl = new URL(url);
    const pathSegments = parsedUrl.pathname
      .split('/')
      .filter((segment) => segment.length > 0);

    // Define a list of possible language codes to look for
    const supportedLangs = ['en', 'vi']; // You can add more language codes here if needed

    // Find the first segment that matches a language code
    for (const segment of pathSegments) {
      if (supportedLangs.includes(segment)) {
        return segment === 'vi' ? null : segment;
      }
    }

    return null; // Return empty string if no language code is found or if it is 'vi'
  } catch (error) {
    console.error('Invalid URL provided');
    return null;
  }
}

const AUTHORIZED_ORIGINS = [
  'https://accounts.ahamove.com',
  'https://accountsstg.ahamove.com',
  'https://accountsuat.ahamove.com',
];

type RedirectType = 'internal' | 'external';

type RedirectUrl = { url: string; type: RedirectType };

function getAppDomainFromOrigin(origin: string): string | null {
  if (origin.includes('accountsstg.ahamove.com')) {
    return 'https://appstg.ahamove.com';
  }
  if (origin.includes('accountsuat.ahamove.com')) {
    return 'https://appuat.ahamove.com';
  }
  if (origin === 'https://accounts.ahamove.com') {
    return 'https://app.ahamove.com';
  }
  return null;
}

export function getRedirectUrl(
  status: AuthStatus,
  pathname: string,
  searchParams: URLSearchParams
): RedirectUrl {
  const defaultRedirectUrl: RedirectUrl = {
    url: pathname,
    type: 'internal',
  };

  if (typeof window === 'undefined' || status !== AuthStatus.Authenticated) {
    return defaultRedirectUrl;
  }

  const fromParam = searchParams.get('from');
  if (fromParam) {
    return { url: decodeURIComponent(fromParam), type: 'internal' };
  }

  const redirectUri = searchParams.get('redirect_uri');
  if (redirectUri) {
    try {
      const url = new URL(decodeURIComponent(redirectUri));
      return { url: url.toString(), type: 'external' };
    } catch (e) {
      console.error('Invalid redirect URI:', redirectUri);
    }
  }

  const appDomain = getAppDomainFromOrigin(window.location.origin);

  if (
    AUTHORIZED_ORIGINS.includes(window.location.origin) &&
    appDomain &&
    (pathname === ROUTES.home ||
      pathname === AuthTab.SignIn.toString() ||
      pathname === AuthTab.SignUp.toString()) &&
    !fromParam &&
    !redirectUri
  ) {
    return { url: appDomain, type: 'external' };
  }

  return defaultRedirectUrl;
}

export const hidePhoneNumber = (phoneNumber: string): string => {
  const phoneNumberLength = phoneNumber.length;

  if (phoneNumberLength < 5) {
    return phoneNumber;
  }

  // For Vietnamese numbers starting with 84
  if (phoneNumber.startsWith('84') && phoneNumberLength >= 7) {
    const countryCode = phoneNumber.substring(0, 2); // '84'
    const lastThreeDigits = phoneNumber.slice(-3); // Last 3 digits
    const middleLength = phoneNumberLength - 5; // Length of the part to mask (excluding 84 and last 3 digits)
    const maskedPart = '*'.repeat(middleLength);

    return `${countryCode}${maskedPart}${lastThreeDigits}`;
  }

  // Fallback for other formats
  const lastThreeDigits = phoneNumber.slice(-3);
  const hiddenPart = '*'.repeat(phoneNumberLength - 3);
  return `${hiddenPart}${lastThreeDigits}`;
};
